// ABOUTME: Modal for undoing scanned items in echeck. Handles two modes: amount picker for
// ABOUTME: non-serial rows, and serial number selection (checkboxes) for serialized products.

import { Box, Checkbox, InputBase, useTheme } from "@mui/material";
import { useState } from "react";

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { useTranslation } from "react-i18next";
import { tokens } from "../../../theme";
import SHModal from "../../theme/Modal/SHModal";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import Shbutton from "../../theme/buttons/Shbutton";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";

const MEcheckUndoAmount = (props) => {
    // props: open, onClose, row, carrier, carrierIndex, bundleIndex,
    //        setCarriers, setToScanRows, setSkuSerialMap, setHighlightColor
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    const [amountToUndo, setAmountToUndo] = useState(1);
    const [selectedSerials, setSelectedSerials] = useState([]);

    // Find all serial rows for this product in the same carrier
    const serialRows = (props.carrier?.rows || []).filter(r =>
        r.product.barcode === props.row?.product?.barcode &&
        Array.isArray(r.serialnumbers) && r.serialnumbers.length > 0
    );

    const isSerialMode = serialRows.length > 0;

    const getSerialBarcode = (row) => {
        const sn = row.serialnumbers?.[0];
        return typeof sn === 'object' ? sn.barcode : sn;
    };

    const toggleSerial = (serialBarcode) => {
        setSelectedSerials(prev =>
            prev.includes(serialBarcode)
                ? prev.filter(s => s !== serialBarcode)
                : [...prev, serialBarcode]
        );
    };

    const getTargetCarrier = (carriers) => {
        if (props.bundleIndex !== undefined && props.bundleIndex !== null && props.bundleIndex !== -1) {
            return carriers[props.bundleIndex]?.carriers?.[props.carrierIndex];
        }
        return carriers[props.carrierIndex];
    };

    const setTargetCarrierRows = (updatedCarriers, newRows) => {
        if (props.bundleIndex !== undefined && props.bundleIndex !== null && props.bundleIndex !== -1) {
            const updatedBundle = { ...updatedCarriers[props.bundleIndex] };
            const updatedSubCarriers = [...updatedBundle.carriers];
            updatedSubCarriers[props.carrierIndex] = {
                ...updatedSubCarriers[props.carrierIndex],
                rows: newRows,
            };
            updatedBundle.carriers = updatedSubCarriers;
            updatedCarriers[props.bundleIndex] = updatedBundle;
        } else {
            updatedCarriers[props.carrierIndex] = {
                ...updatedCarriers[props.carrierIndex],
                rows: newRows,
            };
        }
        return updatedCarriers;
    };

    const performUndo = () => {
        if (isSerialMode) {
            const rowsToUndo = serialRows.filter(r =>
                selectedSerials.includes(getSerialBarcode(r))
            );
            if (rowsToUndo.length === 0) return;

            if (props.clearInstructionShown) {
                const skus = new Set(rowsToUndo.map(r => r.product?.sku).filter(Boolean));
                skus.forEach(sku => props.clearInstructionShown(sku));
            }

            // Remove selected serial rows from the correct carrier
            props.setCarriers(prev => {
                const updatedCarriers = [...prev];
                const target = getTargetCarrier(updatedCarriers);
                const newRows = target.rows.filter(r => {
                    if (r.product.barcode !== props.row.product.barcode) return true;
                    return !selectedSerials.includes(getSerialBarcode(r));
                });
                return setTargetCarrierRows(updatedCarriers, newRows);
            });

            // Restore to originalOrderlines (no nested setToScanRows)
            if (props.setOriginalOrderlines) {
                props.setOriginalOrderlines(prevOriginal => {
                    const updatedOriginal = [...(prevOriginal || [])];
                    for (const row of rowsToUndo) {
                        updatedOriginal.push({ ...row, amount: 1 });
                    }
                    return updatedOriginal;
                });
            }

            // Add serial rows back to toScanRows
            props.setToScanRows(prev => {
                const updatedRows = [...(prev || [])];
                for (const row of rowsToUndo) {
                    updatedRows.push({ ...row, amount: 1 });
                }
                return updatedRows;
            });

            // Restore serial numbers to skuSerialMap
            props.setSkuSerialMap(prevMap => {
                const newMap = { ...prevMap };
                for (const row of rowsToUndo) {
                    const sku = row.product.sku;
                    const serial = row.serialnumbers?.[0];
                    if (!sku || !serial) continue;
                    if (!newMap[sku]) newMap[sku] = [];
                    newMap[sku] = [...newMap[sku], serial];
                }
                return newMap;
            });
        } else {
            // Amount mode
            const amount = parseInt(amountToUndo, 10) || 0;
            if (amount <= 0) return;

            if (props.clearInstructionShown) props.clearInstructionShown(props.row?.product?.sku);

            // Decrease or remove the row from the correct carrier
            props.setCarriers(prev => {
                const updatedCarriers = [...prev];
                const target = getTargetCarrier(updatedCarriers);
                const newRows = target.rows.reduce((acc, r) => {
                    const barcodeMatch = r.product.barcode === props.row.product.barcode;
                    const externalIdMatch =
                        (r.externalId && props.row.externalId)
                            ? r.externalId === props.row.externalId
                            : true;
                    const typeMatch = r.type === props.row.type;
                    if (barcodeMatch && externalIdMatch && typeMatch) {
                        const newAmount = r.amount - amount;
                        if (newAmount > 0) acc.push({ ...r, amount: newAmount });
                    } else {
                        acc.push(r);
                    }
                    return acc;
                }, []);
                return setTargetCarrierRows(updatedCarriers, newRows);
            });

            // Restore to original orderlines and recalculate merged display
            // Restore to originalOrderlines (no nested setToScanRows)
            if (props.setOriginalOrderlines) {
                props.setOriginalOrderlines(prevOriginal => {
                    return [...(prevOriginal || []), { ...props.row, amount }];
                });
            }

            // Add back to toScanRows - match by barcode and type since display is merged by SKU+type
            props.setToScanRows(prev => {
                const updatedRows = [...(prev || [])];
                const existingIndex = updatedRows.findIndex(r =>
                    r.product.barcode === props.row.product.barcode &&
                    r.type === props.row.type
                );

                if (existingIndex !== -1) {
                    updatedRows[existingIndex] = {
                        ...updatedRows[existingIndex],
                        amount: updatedRows[existingIndex].amount + amount
                    };
                } else {
                    updatedRows.push({ ...props.row, amount });
                }
                return updatedRows;
            });
        }

        if(props.playSuccess) props.playSuccess();
        props.setHighlightColor('green');
        handleClose();
    };

    const handleClose = () => {
        setAmountToUndo(1);
        setSelectedSerials([]);
        props.onClose();
    };

    return (
        <SHModal
            open={props.open}
            onClose={handleClose}
        >
            <Box sx={{
                textAlign:"center",
                paddingY:4,
                paddingX:5,
                display:"flex",
                flexDirection:"column",
                alignItems:'center',
            }}>
                <Subheading>
                    {t("undo_amount")}
                </Subheading>
                {props.row &&
                    <Text>{props.row.product?.description}</Text>
                }
            </Box>

            <Box sx={{
                background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                padding:3,
                pb:1,
                borderTopLeftRadius:16,
                borderTopRightRadius:16,
                display:"flex",
                flexDirection:"column",
                gap:2
            }}>
                {isSerialMode ? (
                    <Box sx={{ display:"flex", flexDirection:"column", gap:1 }}>
                        {serialRows.map((row, idx) => {
                            const serialBarcode = getSerialBarcode(row);
                            const isChecked = selectedSerials.includes(serialBarcode);
                            return (
                                <Box
                                    key={idx}
                                    onClick={() => toggleSerial(serialBarcode)}
                                    sx={{
                                        height:40,
                                        background: isChecked
                                            ? colors.blue[100]
                                            : (theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]),
                                        border: isChecked
                                            ? `1px solid ${colors.blue[400]}`
                                            : `1px solid ${theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[200]}`,
                                        display:"flex",
                                        alignItems:'center',
                                        pl:0.5,
                                        pr:1.5,
                                        borderRadius:5,
                                        cursor:"pointer",
                                        transition:"background 0.15s, border 0.15s",
                                    }}
                                >
                                    <Checkbox
                                        checked={isChecked}
                                        onChange={() => toggleSerial(serialBarcode)}
                                        size="small"
                                        sx={{
                                            color: colors.grey[300],
                                            '&.Mui-checked': { color: colors.blue[400] },
                                        }}
                                    />
                                    <Text semibold>{serialBarcode}</Text>
                                </Box>
                            );
                        })}
                    </Box>
                ) : (
                    <Box sx={{ width:1 }}>
                        <Box sx={{
                            height:40,
                            background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                            display:"flex",
                            alignItems:'center',
                            pl:0.5,
                            borderRadius:5,
                        }}>
                            <Box sx={{
                                height:32,
                                lineHeight:"32px",
                                borderRadius:4,
                                px:2,
                                background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                boxShadow:theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                            }}>
                                <Text>{t("amount")}</Text>
                            </Box>
                            <InputBase
                                autoFocus
                                value={amountToUndo}
                                onChange={(e) => {
                                    const input = e.target.value;
                                    if (/^\d*$/.test(input)) {
                                        if (input === '') { setAmountToUndo(''); return; }
                                        const numericInput = parseInt(input, 10);
                                        if (numericInput >= 1) {
                                            setAmountToUndo(Math.min(numericInput, props.row?.amount || 1));
                                        }
                                    }
                                }}
                                sx={{
                                    height:40, flex:1, px:2,
                                    fontSize:14, lineHeight:"40px", fontFamily:"Roboto",
                                }}
                            />
                        </Box>
                    </Box>
                )}
            </Box>

            <Box component="footer" sx={{
                display:"flex",
                width:1,
                paddingBottom: isStandAlone ? "32px" : "16px",
                alignItems:"center",
                paddingRight:3,
                zIndex:1058,
                position:"relative",
                paddingTop:2,
                background:`${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                borderBottomLeftRadius:24,
                borderBottomRightRadius:24,
                justifyContent:"space-between",
                "@media screen and (max-width: 47.9375em)" : { paddingRight:0 }
            }}>
                <Shbutton
                    isCancel={true}
                    className={"always-visible"}
                    onClick={handleClose}
                    color="primary"
                    variant="text"
                    startIcon={<ArrowRightAltIcon/>}
                >
                    {t("cancel")}
                </Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton
                        className={"group-btn"}
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                        variant="contained"
                        onClick={performUndo}
                        disabled={isSerialMode && selectedSerials.length === 0}
                    >
                        {t("undo")}
                    </Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    )
}

export default MEcheckUndoAmount;
